/**
 * @license
 * Copyright (C) 2022 Anna Plodari
 * Software proprietario - Tutti i diritti riservati.
 * Registrato SIAE - Sezione OLAF.
 *
 * È vietato qualsiasi utilizzo non autorizzato, copia, modifica, estrazione,
 * riuso, rielaborazione, distribuzione, reverse engineering o decompilazione
 * del software, delle API, delle logiche applicative e dei dati del database.
 *
 * L'accesso al codice non costituisce concessione di licenza.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import CompanyNavBar, { navLinks } from '../../components/CompanyNavBar';
import BannerJobPost from '../../components/BannerJobPost';
import JobPostsList from '../../components/JobPosts/JobPostsList';
import useJobPosts from '../../lib/hooks/useJobPosts';
import { useAuth } from '../../commons/Auth';
import useCompany from '../../context/CompanyContext';
import FullScreenSpinner from '../../components/FullScreenSpinner';
import { TJobPost } from '../../types/JobPost';
import JobPost from '../../components/JobPosts/JobPost';
import useMe from '../../lib/hooks/useMe';
import PrimaryButton from '../../components/PrimaryButton';
import BannerBrandPromote from '../../components/BannerBrandPromote';

type TutorialStep = {
  title: string;
  text: React.ReactNode;  // 👈 non più solo string
  image: string;
};

const tutorialSteps: TutorialStep[] = [
  {
    title: 'Acquisto e attivazione della Brand Card',
    text: (<span>Per sottoscrivere l&apos;abbonamento, clicca su <b>&quot;Acquista&quot;</b> nella Brand Card: riceverai un’email di conferma.</span>),
    image: '/images/tutorial/tutorial-step1.png',
  },
  {
    title: 'Pulsante Configura Brand',
    text: (<span>Clicca su <b>&quot;Configura Brand&quot;</b> nel cruscotto per inserire i dati del tuo brand.</span>),
    image: '/images/tutorial/tutorial-step2.png',
  },
  {
    title: 'Inserimento dati Brand',
    text: 'Compila il popup con nome azienda, settore, descrizione, sito o "Lavora con noi" e logo.',
    image: '/images/tutorial/tutorial-step3.png',
  },
  {
    title: 'Sezione Brand Aziendale',
    text: (<span>La sezione <b>&quot;Brand Azienda&quot;</b> mostra i dati inseriti. Puoi aggiornarli in ogni momento con <b>&quot;Configura Brand&quot;</b>.</span>),
    image: '/images/tutorial/tutorial-step4.png',
  },
    {
    title: 'Brand Aziendale nel cruscotto candidato',
    text: 'Nel cruscotto candidato sarà visibile il logo aziendale, cliccabile e collegato alla pagina inserita.',
    image: '/images/tutorial/tutorial-step5.png',
  },
];


const PromoteBrand: React.FC = () => {
  const { token }: any = useAuth();
  useMe(token);
  const companyContext = useCompany();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [step, setStep] = useState(0);
  const query = {
    populate: ['hr_company', 'hr_users', 'settori'],
    filters: { hr_company: { id: { $eq: companyContext.data.hr_id } } },
    sort: { createdAt: 'desc' },
  };

  const { data, isLoading, isError, isSuccess } = useJobPosts(query, token);

 const closeWizard = () => {
    setIsWizardOpen(false);
    setStep(0);
  };

  return (
    <div>
      <CompanyNavBar navLink={navLinks.brand} />
      {isLoading && <FullScreenSpinner />}

      {isSuccess && (
        <div className="flex flex-col h-screen bg-onboardingmobile4 lg:bg-onboarding4 bg-no-repeat bg-right-top lg:bg-[right_-16rem_top] xl:bg-right-top bg-cover lg:bg-contain">
          <BannerBrandPromote
            showBanner={data!.length !== 0}
            isWizardOpen={isWizardOpen}
            setIsWizardOpen={setIsWizardOpen}
          />

          <div className="px-4 lg:px-0 pb-8">
            <div className="shadow-card p-8 rounded-xl w-full lg:max-w-4xl mx-auto">
              <div className="mb-8">
                <h2 className="font-primarybold text-base text-primary pb-4">
                  Ogni azienda abbonata dispone di uno spazio personalizzato in bacheca, pensato per valorizzare il
                  brand e attrarre i talenti presenti in piattaforma.
                  <br />
                  <br />
                  La pagina dedicata include:
                </h2>
                <div className="flex flex-col">
                  <h4 className="font-primarybold text-base text-black user-data-value">- Logo aziendale cliccabile</h4>
                  <h4 className="font-primarybold text-base text-black user-data-value">
                    - Nome dell’azienda, settore di riferimento e breve descrizione (max 250 caratteri)
                  </h4>
                  <h4 className="font-primarybold text-base text-black user-data-value">
                    - Link al sito aziendale o sezione “Lavora con noi” (a scelta dell’azienda)
                  </h4>
                </div>
                <h2 className="font-primarybold text-base text-black py-4 w-4/5">
                  Per attivare il servizio, clicca su <b className="text-primary">Promuovi il tuo brand</b> oppure
                  Accedi alla sezione{' '}
                  <Link to="/company-upgrade?brandCard=true">
                    <b className="text-primary">Upgrade</b>
                  </Link>{' '}
                  per acquistare e configurare la tua Brand Card.
                </h2>
              </div>
              <button
                onClick={() => setIsWizardOpen(true)}
                className="bg-yellow text-white px-4 py-2 rounded-lg shadow-md hover:bg-yellow/80">
                Tutorial Brand Card
              </button>
              <div className="relative flex flex-col items-center mt-10 space-y-4 max-w-lg text-center mx-auto pb-10 lg:pb-0 mb-6">
                <p className="text-black text-center">
                  Hai bisogno di aiuto? Contattaci a{' '}
                  <a href="mailto:support@hrmeccatronica.com" className="text-primary underline">
                    support@hrmeccatronica.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Wizard tipo carosello */}
      {isWizardOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl relative overflow-hidden">
            {/* Bottone Close */}
            <button onClick={closeWizard} className="w-5 absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              <img src="/close-icon.svg" alt="close icon" className="block h-6 w-6" aria-hidden="true" />
            </button>
            {/* Wrapper scorrevole */}
            <div className="overflow-hidden relative w-full max-w-lg mx-auto">
              <div
                className="flex transition-transform duration-500"
                style={{ transform: `translateX(-${step * 100}%)` }}>
                {tutorialSteps.map((s, idx) => (
                  <div key={idx} className="w-full flex-shrink-0 px-4">
                    <h2 className="text-xl font-bold text-primary mb-4">{s.title}</h2>
                    <p className="text-gray-700 mb-4">{s.text}</p>
                    <img src={s.image} alt={`step-${idx}`} className="w-full h-56 object-contain mb-6" />
                  </div>
                ))}
              </div>
            </div>
            {/* Controlli */}
            <div className="flex justify-between items-center mt-4">
              <button
                onClick={() => setStep((s) => Math.max(s - 1, 0))}
                disabled={step === 0}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 disabled:opacity-50">
                Indietro
              </button>
              {step < tutorialSteps.length - 1 ? (
                <button
                  onClick={() => setStep((s) => Math.min(s + 1, tutorialSteps.length - 1))}
                  className="px-4 py-2 rounded bg-primary text-white">
                  Avanti
                </button>
              ) : (
                <button onClick={closeWizard} className="px-4 py-2 rounded bg-green-500 text-white">
                  Fine
                </button>
              )}
            </div>

            {/* Indicatori */}
            <div className="flex justify-center mt-4 space-x-2">
              {tutorialSteps.map((_, idx) => (
                <span key={idx} className={`w-3 h-3 rounded-full ${idx === step ? 'bg-primary' : 'bg-gray-300'}`} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoteBrand;